import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LotterySelector } from "@/components/app/LotterySelector";
import { useLottery } from "@/hooks/use-lottery-selection";
import {
  POOL_LIMITS,
  ticketPrice,
  generateComplete,
  generateStrategic,
  previewStrategic,
  generatePairs,
  generateFixed,
  estimatePairs,
  estimateFixed,
  getPairsAnchor,
  combinations,
  type LotterySlug,
} from "@/lib/pool-math";
import { STRATEGIC, strategicVHint, strategicTHint } from "@/lib/strategic-matrix";
import { cn, formatInt, formatBRL } from "@/lib/utils";
import { Sparkles, Pin, Download, Layers, AlertCircle, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { GameDnaButton } from "@/components/app/GameDnaButton";
import { ExportGamesDialog } from "@/components/app/ExportGamesDialog";
import { PairBuilder } from "@/components/app/PairBuilder";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import { useNumberStats, statTitle } from "@/hooks/use-number-stats";
import { Trash2 } from "lucide-react";
import type { SavedGameScope } from "@/lib/check-scope";

type Mode = "complete" | "closure" | "pairs" | "fixed";

export default function Combinacoes() {
  const { selected, config } = useLottery();
  const slug = selected as LotterySlug;
  const limits = POOL_LIMITS[slug];
  const strategic = STRATEGIC[slug];
  const { user } = useAuth();
  const { canAddSavedGames, refreshUsage } = usePlanLimits();

  // Hidratação inicial via localStorage (mantém as dezenas escolhidas ao navegar
  // e também sobrevive a recarregar a página ou abrir em outra aba).
  const readInitialPicks = () => {
    if (typeof window === "undefined") return { selected: [] as number[], fixed: [] as number[], pairs: [] as [number, number][] };
    try {
      const raw = localStorage.getItem(`lt-combinacoes-picks-${slug}`);
      if (!raw) return { selected: [], fixed: [], pairs: [] };
      const p = JSON.parse(raw);
      return {
        selected: Array.isArray(p.selected) ? p.selected : [],
        fixed: Array.isArray(p.fixed) ? p.fixed : [],
        pairs: Array.isArray(p.pairs) ? p.pairs : [],
      };
    } catch {
      return { selected: [], fixed: [], pairs: [] };
    }
  };
  const __initialPicks = readInitialPicks();
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>(__initialPicks.selected);
  const [fixedNumbers, setFixedNumbers] = useState<number[]>(__initialPicks.fixed);
  const [customPairs, setCustomPairs] = useState<[number, number][]>(__initialPicks.pairs);
  const [mode, setMode] = useState<Mode>("complete");
  const [k, setK] = useState<number>(limits.baseNumbers);
  const [guaranteeHits, setGuaranteeHits] = useState<number>(strategic.tOptions[0]);
  const [scopeOpen, setScopeOpen] = useState(false);
  const SS_KEY = `lt-combinacoes-games-${slug}`;
  const PICK_KEY = `lt-combinacoes-picks-${slug}`;

  type PickState = { selected: number[]; fixed: number[]; pairs: [number, number][] };
  const readPicks = (s: string): PickState => {
    if (typeof window === "undefined") return { selected: [], fixed: [], pairs: [] };
    try {
      const raw = localStorage.getItem(`lt-combinacoes-picks-${s}`);
      if (!raw) return { selected: [], fixed: [], pairs: [] };
      const p = JSON.parse(raw) as Partial<PickState>;
      return {
        selected: Array.isArray(p.selected) ? p.selected : [],
        fixed: Array.isArray(p.fixed) ? p.fixed : [],
        pairs: Array.isArray(p.pairs) ? (p.pairs as [number, number][]) : [],
      };
    } catch {
      return { selected: [], fixed: [], pairs: [] };
    }
  };

  const [games, setGames] = useState<number[][]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? (JSON.parse(raw) as number[][]) : [];
    } catch {
      return [];
    }
  });
  const [strategicInfo, setStrategicInfo] = useState<{
    target: number;
    actual: number;
    badge: "melhor" | "exato" | "aproximacao";
    coverageVerified?: boolean;
    coverageTested?: number;
  } | null>(null);
  // Dezena âncora da última geração por duplas (seleção ímpar).
  const [pairAnchor, setPairAnchor] = useState<number | null>(null);
  // Persistir picks por loteria
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        PICK_KEY,
        JSON.stringify({ selected: selectedNumbers, fixed: fixedNumbers, pairs: customPairs }),
      );
    } catch {
      /* ignore */
    }
  }, [selectedNumbers, fixedNumbers, customPairs, PICK_KEY]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (games.length === 0) sessionStorage.removeItem(SS_KEY);
      else sessionStorage.setItem(SS_KEY, JSON.stringify(games));
    } catch {
      /* ignore */
    }
  }, [games, SS_KEY]);

  // Ao trocar de loteria, restaura picks salvos daquela loteria (mantém entre navegações).
  useEffect(() => {
    const p = readPicks(slug);
    setSelectedNumbers(p.selected);
    setFixedNumbers(p.fixed);
    setCustomPairs(p.pairs);
    setK(limits.baseNumbers);
    setGuaranteeHits(STRATEGIC[slug].tOptions[0]);
    if (typeof window !== "undefined") {
      try {
        const raw = sessionStorage.getItem(`lt-combinacoes-games-${slug}`);
        setGames(raw ? (JSON.parse(raw) as number[][]) : []);
      } catch {
        setGames([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const { data: numberStats } = useNumberStats(slug);

  const clearPicks = () => {
    setSelectedNumbers([]);
    setFixedNumbers([]);
    setCustomPairs([]);
    if (typeof window !== "undefined") {
      try { localStorage.removeItem(PICK_KEY); } catch { /* ignore */ }
    }
  };

  // Quando entra/sai de Fechamento Estratégico, força k para o valor
  // fixo da matriz curada (Mega/Dupla = 6, Quina = 5, Lotofácil = 15).
  useEffect(() => {
    if (mode === "closure") {
      setK(strategic.k);
      if (!strategic.tOptions.includes(guaranteeHits)) {
        setGuaranteeHits(strategic.tOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, slug]);

  const strategicVOk = mode !== "closure" || strategic.vOptions.includes(selectedNumbers.length);
  const strategicMaxSelect = mode === "closure"
    ? strategic.vOptions[strategic.vOptions.length - 1]
    : limits.maxSelected;
  const strategicMinSelect = mode === "closure"
    ? strategic.vOptions[0]
    : limits.minSelected;

  const universe = useMemo(
    () => Array.from({ length: config.max - config.min + 1 }, (_, i) => config.min + i),
    [config],
  );

  // Orientação da faixa de dezenas para "Combinação por duplas".
  const pairsHint =
    slug === "lotofacil"
      ? "Lotofácil: selecione um número ímpar de dezenas entre 17 e 25 (jogos de 15 = âncora + 7 duplas)."
      : slug === "quina"
        ? "Quina: selecione um número ímpar de dezenas entre 7 e 39 (jogos de 5 = âncora + 2 duplas)."
        : "Selecione uma quantidade par de dezenas (jogos de 6 = 3 duplas).";

  const toggleNumber = (n: number) => {
    setSelectedNumbers((prev) => {
      if (prev.includes(n)) {
        setFixedNumbers((f) => f.filter((x) => x !== n));
        return prev.filter((x) => x !== n);
      }
      const cap = mode === "closure" ? strategicMaxSelect : limits.maxSelected;
      if (prev.length >= cap) {
        toast.error(
          mode === "closure"
            ? `Para Fechamento Estratégico de ${config.name}, escolha ${strategicVHint(slug)} dezenas.`
            : `Máximo de ${limits.maxSelected} dezenas para ${config.name}`,
        );
        return prev;
      }
      return [...prev, n].sort((a, b) => a - b);
    });
  };

  const toggleFixed = (n: number) => {
    if (!selectedNumbers.includes(n)) return;
    setFixedNumbers((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b),
    );
  };

  const preview = useMemo(() => {
    const minSel = mode === "closure" ? strategicMinSelect : limits.minSelected;
    if (selectedNumbers.length < minSel) return null;
    let total = 0;
    let isUpper = false;
    let isStrategic = false;
    if (mode === "complete") total = combinations(selectedNumbers.length, k);
    else if (mode === "pairs") {
      if (selectedNumbers.length % 2 !== k % 2) return null;
      total = estimatePairs(selectedNumbers.length, k);
    } else if (mode === "fixed") {
      total = estimateFixed(fixedNumbers.length, selectedNumbers.length - fixedNumbers.length, k);
    } else if (mode === "closure") {
      // Fechamento Estratégico: previsão = target publicado da matriz.
      const tgt = previewStrategic(slug, selectedNumbers.length, guaranteeHits);
      if (tgt === null) return null;
      total = tgt;
      isStrategic = true;
    } else {
      total = combinations(selectedNumbers.length, k);
    }
    if (!total) return null;
    return { total, cost: total * ticketPrice(slug, k), isUpper, isStrategic };
  }, [selectedNumbers, fixedNumbers, k, mode, slug, limits, guaranteeHits, strategicMinSelect]);

  const handleGenerate = () => {
    if (mode === "closure") {
      if (!strategicVOk) {
        toast.error(
          `Para Fechamento Estratégico de ${config.name}, escolha exatamente ${strategicVHint(slug)} dezenas.`,
        );
        return;
      }
    } else {
      if (selectedNumbers.length < limits.minSelected) {
        toast.error(`Selecione ao menos ${limits.minSelected} dezenas`);
        return;
      }
    }
    // Para os demais modos: confirma quando volume é alto (custo/tempo).
    if (mode !== "closure") {
      let estimate = 0;
      if (mode === "complete") estimate = combinations(selectedNumbers.length, k);
      else if (mode === "pairs") estimate = estimatePairs(selectedNumbers.length, k);
      else if (mode === "fixed")
        estimate = estimateFixed(fixedNumbers.length, selectedNumbers.length - fixedNumbers.length, k);
      if (estimate > 1000) {
        const cost = estimate * ticketPrice(slug, k);
        const ok = window.confirm(
          `Você está prestes a gerar ${formatInt(estimate)} jogos.\n\n` +
          `Custo total estimado: ${formatBRL(cost)}.\n\n` +
          `Volumes muito altos podem demorar alguns segundos para gerar.\n\n` +
          `Deseja continuar? Cancele para escolher outra estratégia (fechamento estratégico ou por duplas) e reduzir o número de jogos.`,
        );
        if (!ok) return;
      }
    }
    try {
      let g: number[][] = [];
      if (mode === "complete") g = generateComplete(selectedNumbers, k);
      else if (mode === "closure") {
        const r = generateStrategic(slug, selectedNumbers, guaranteeHits);
        g = r.games;
        setStrategicInfo({
          target: r.target,
          actual: r.actual,
          badge: r.badge,
          coverageVerified: r.coverageVerified,
          coverageTested: r.coverageTested,
        });
      } else if (mode === "pairs") {
        if (selectedNumbers.length % 2 !== k % 2) {
          toast.error(
            selectedNumbers.length % 2 !== 0
              ? "Seleção ímpar: escolha uma quantidade ímpar de dezenas por jogo"
              : "Seleção par: escolha uma quantidade par de dezenas por jogo",
          );
          return;
        }
        const usePairs =
          customPairs.length === Math.floor(selectedNumbers.length / 2) && customPairs.length > 0
            ? customPairs
            : null;
        g = generatePairs(selectedNumbers, k, usePairs);
        setPairAnchor(getPairsAnchor(selectedNumbers, usePairs));
      } else if (mode === "fixed") {
        if (fixedNumbers.length === 0) {
          toast.error("Marque ao menos uma dezena como fixa");
          return;
        }
        if (fixedNumbers.length >= k) {
          toast.error(`Fixas devem ser menos que ${k} dezenas/jogo`);
          return;
        }
        g = generateFixed(fixedNumbers, selectedNumbers.filter((n) => !fixedNumbers.includes(n)), k);
      }
      if (g.length === 0) {
        toast.error("Nenhum jogo gerado — ajuste os parâmetros");
        return;
      }
      if (mode !== "closure") setStrategicInfo(null);
      if (mode !== "pairs") setPairAnchor(null);
      setGames(g);
      toast.success(`${formatInt(g.length)} jogo(s) gerado(s)`);
    } catch (e: any) {
      toast.error(e.message || "Erro ao gerar combinações");
    }
  };

  const downloadCSV = () => {
    if (!games.length) return;
    const lines = [
      `# ${config.name} — combinacoes geradas`,
      `# total: ${games.length} jogos · ${k} dezenas/jogo`,
      "jogo,dezenas",
      ...games.map((g, i) => `${i + 1},"${g.map((n) => String(n).padStart(2, "0")).join(" ")}"`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `combinacoes-${slug}-${games.length}jogos.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSaveDialog = () => {
    if (!user) return toast.error("Faça login para salvar");
    if (!games.length) return;
    const check = canAddSavedGames(games.length);
    if (!check.ok) {
      toast.error(`Limite do seu plano atingido (${check.remaining ?? 0} vagas restantes). Faça upgrade para salvar mais.`);
      return;
    }
    setScopeOpen(true);
  };

  const persistGames = async (scopeInfo: { scope: SavedGameScope; from: number | null; until: number | null; specialScheduleId?: string | null }) => {
    if (!user || !games.length) return;
    const { data: lottery } = await supabase.from("lotteries").select("id").eq("slug", slug).maybeSingle();
    if (!lottery) return toast.error("Loteria não encontrada");
    const rows = games.map((numbers) => ({
      user_id: user.id,
      lottery_id: lottery.id,
      numbers,
      label: `Combinações ${new Date().toLocaleDateString("pt-BR")}`,
      check_scope: scopeInfo.scope,
      check_from_contest: scopeInfo.from,
      check_until_contest: scopeInfo.until,
        special_schedule_id: scopeInfo.specialScheduleId ?? null,
    }));
    const { error } = await supabase.from("saved_games").insert(rows);
    if (error) return toast.error(error.message);
    refreshUsage();
    toast.success(`${games.length} jogo(s) salvo(s) — disponíveis em Meus jogos`);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <h1 className="font-serif text-3xl">Combinações</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Gere combinações de jogos com diferentes estratégias e exporte em PDF ou CSV. Sem participantes ou cotas — apenas o gerador.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Loteria</span>
            <LotterySelector />
          </CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span>
              Dezenas escolhidas{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({selectedNumbers.length}/{limits.maxSelected} — mínimo {limits.minSelected})
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearPicks}
              disabled={selectedNumbers.length === 0}
              className="gap-1.5 text-muted-foreground hover:text-destructive"
              title="Remove todas as dezenas escolhidas — só executa quando você clicar aqui."
            >
              <Trash2 className="h-4 w-4" /> Apagar números escolhidos
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mode === "fixed" && (
            <div
              className={cn(
                "mb-4 flex items-start gap-3 rounded-lg border-2 p-3 transition-colors",
                fixedNumbers.length === 0
                  ? "border-lt-teal bg-lt-teal/10 animate-pulse"
                  : "border-lt-teal/60 bg-lt-teal/5",
              )}
            >
              <Pin className="mt-0.5 h-5 w-5 shrink-0 fill-lt-teal text-lt-teal" />
              <div className="flex-1 text-[13px]">
                <div className="font-semibold text-foreground">
                  Marque ao menos UMA dezena fixa
                </div>
                <div className="text-muted-foreground">
                  As dezenas fixas aparecem em <strong>todos os jogos gerados</strong>. Clique
                  com o <strong>botão direito</strong> (ou <strong>toque longo</strong>) sobre uma dezena já
                  selecionada — ou use o painel abaixo do quadro.
                </div>
              </div>
              <span
                className={cn(
                  "shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold",
                  fixedNumbers.length === 0
                    ? "bg-lt-teal/20 text-lt-teal"
                    : "bg-lt-teal text-background",
                )}
              >
                {fixedNumbers.length} fixa(s)
              </span>
            </div>
          )}
          <div className="grid grid-cols-10 gap-1.5 sm:grid-cols-12 md:grid-cols-15 lg:grid-cols-20">
            {universe.map((n) => {
              const active = selectedNumbers.includes(n);
              const isFixed = fixedNumbers.includes(n);
              return (
                <button
                  key={n}
                  onClick={() => toggleNumber(n)}
                  onContextMenu={(e) => {
                    if (mode !== "fixed") return;
                    e.preventDefault();
                    toggleFixed(n);
                  }}
                  title={statTitle(n, numberStats?.get(n))}
                  className={cn(
                    "relative flex h-9 items-center justify-center rounded-md border text-[12px] font-medium transition-all",
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-lt-border bg-lt-bg2 hover:border-primary/50 hover:bg-primary/5",
                    isFixed && active && "ring-2 ring-lt-teal ring-offset-1 ring-offset-background",
                  )}
                >
                  {String(n).padStart(2, "0")}
                  {isFixed && active && (
                    <Pin className="absolute -right-1 -top-1 h-3 w-3 fill-lt-teal text-lt-teal" />
                  )}
                </button>
              );
            })}
          </div>
          {mode === "fixed" && selectedNumbers.length > 0 && (
            <div className="mt-4 rounded-lg border border-lt-teal/40 bg-lt-teal/5 p-3">
              <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-lt-teal">
                <Pin className="h-3.5 w-3.5 fill-lt-teal" />
                Selecione as dezenas que devem ficar FIXAS em todos os jogos:
              </div>
              <div className="flex flex-wrap items-center gap-2">
              {selectedNumbers.map((n) => {
                const f = fixedNumbers.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => toggleFixed(n)}
                    className={cn(
                      "flex h-8 items-center gap-1 rounded-full border-2 px-3 text-[12px] font-medium transition-all",
                      f
                        ? "border-lt-teal bg-lt-teal text-background shadow"
                        : "border-dashed border-lt-teal/50 bg-lt-bg2 text-foreground hover:border-lt-teal hover:bg-lt-teal/10",
                    )}
                  >
                    <Pin className={cn("h-3 w-3", f ? "fill-background" : "fill-lt-teal/60 text-lt-teal/60")} />
                    {String(n).padStart(2, "0")}
                  </button>
                );
              })}
              </div>
            </div>
          )}
          {mode === "pairs" && selectedNumbers.length > 0 && (
            <div className="mt-4">
              <PairBuilder
                selectedNumbers={selectedNumbers}
                value={customPairs}
                onChange={setCustomPairs}
                hint={pairsHint}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estratégia de geração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="grid gap-3 md:grid-cols-2">
            {([
              { v: "complete", t: "Combinações completas", d: "Todas as combinações C(n, k). Cobertura total, mais jogos." },
              { v: "closure", t: "Fechamento estratégico", d: "Menos jogos buscando uma meta mínima de acertos se Y forem sorteados." },
              { v: "pairs", t: "Combinação por duplas", d: "Suas dezenas viram duplas. Em quantidade ímpar, a dezena que sobra é a âncora e entra em todos os jogos." },
              { v: "fixed", t: "Dezenas fixas + variáveis", d: "Marque algumas dezenas como fixas (em todos os jogos). As demais combinam livremente." },
            ] as const).map((opt) => (
              <Label
                key={opt.v}
                className={cn(
                  "cursor-pointer rounded-lg border p-4 transition-all",
                  mode === opt.v ? "border-primary bg-primary/5" : "border-lt-border",
                )}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem value={opt.v} className="mt-1" />
                  <div>
                    <div className="font-medium">{opt.t}</div>
                    <div className="text-xs text-muted-foreground">{opt.d}</div>
                  </div>
                </div>
              </Label>
            ))}
          </RadioGroup>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Dezenas por jogo</Label>
              <Select
                value={String(k)}
                onValueChange={(v) => setK(Number(v))}
                disabled={mode === "closure"}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: limits.maxPickPerGame - limits.baseNumbers + 1 }, (_, i) => limits.baseNumbers + i).map((v) => (
                    <SelectItem
                      key={v}
                      value={String(v)}
                      disabled={
                        v > selectedNumbers.length ||
                        (mode === "pairs" && selectedNumbers.length > 0 && v % 2 !== selectedNumbers.length % 2) ||
                        (mode === "fixed" && v <= fixedNumbers.length)
                      }
                    >
                      {v} dezenas — {formatBRL(ticketPrice(slug, v))}/jogo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === "closure" && (
                <p className="text-[11px] text-muted-foreground">
                  Fixo em {strategic.k} dezenas/jogo para {config.name} (aposta mínima da Caixa).
                </p>
              )}
            </div>

            {mode === "closure" && (
              <div className="space-y-2 md:col-span-2">
                <Label>Buscar ao menos</Label>
                <Select
                  value={String(guaranteeHits)}
                  onValueChange={(v) => setGuaranteeHits(Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {strategic.tOptions.map((v) => (
                      <SelectItem key={v} value={String(v)}>{v} acertos</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Para {config.name}: {strategicTHint(slug)} acertos.
                </p>
              </div>
            )}
          </div>

          {mode === "closure" && (
            <div
              className={cn(
                "rounded-lg border p-3 text-[12px]",
                strategicVOk
                  ? "border-lt-teal/40 bg-lt-teal/5 text-foreground"
                  : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
              )}
            >
              <strong>Fechamento Estratégico — {config.name}:</strong>{" "}
              escolha exatamente {strategicVHint(slug)} dezenas. Você selecionou{" "}
              <strong>{selectedNumbers.length}</strong>.
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-lt-gold-border bg-lt-gold-bg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> Previsão
              </div>
              <div className="mt-2 grid gap-3 text-[13px] sm:grid-cols-2">
                <div>
                  <div className="text-muted-foreground">
                    {preview.isStrategic
                      ? "Jogos (referência publicada)"
                      : preview.isUpper
                      ? "Jogos (limite superior)"
                      : "Jogos a gerar"}
                  </div>
                  <div className="text-lg font-medium">{formatInt(preview.total)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Custo total estimado</div>
                  <div className="text-lg font-medium">{formatBRL(preview.cost)}</div>
                </div>
              </div>
              {preview.isStrategic && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Sistema publicado de referência. Após gerar, o app mostra
                  quantos jogos efetivamente produziu.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={mode === "closure" && !strategicVOk}
            >
              Gerar combinações
            </Button>
          </div>
        </CardContent>
      </Card>

      {games.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                {games.length} jogo(s) gerado(s)
                {strategicInfo && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      title={
                        strategicInfo.badge === "exato"
                          ? `Resultado exato — bate a referência publicada (${strategicInfo.target} jogos).`
                          : strategicInfo.badge === "melhor"
                          ? `Melhor que a referência: gerou ${strategicInfo.actual} jogos vs. ${strategicInfo.target} da literatura especializada.`
                          : `Aproximação — referência publicada: ${strategicInfo.target}; gerador entregou ${strategicInfo.actual}.`
                      }
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                        strategicInfo.badge === "aproximacao"
                          ? "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                          : "bg-lt-teal/20 text-lt-teal",
                      )}
                    >
                      {strategicInfo.badge === "exato"
                        ? `Exato · ref. ${strategicInfo.target}`
                        : strategicInfo.badge === "melhor"
                        ? `Otimizado · ref. ${strategicInfo.target}`
                        : `Aproximação · ref. ${strategicInfo.target}`}
                    </span>
                    {strategicInfo.badge === "melhor" && strategicInfo.coverageVerified && (
                      <span
                        title={`Verificamos exaustivamente todos os ${strategicInfo.coverageTested?.toLocaleString("pt-BR")} subconjuntos possíveis: cada um aparece em pelo menos um jogo. A garantia está matematicamente comprovada.`}
                        className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                      >
                        ✓ Cobertura verificada
                      </span>
                    )}
                    {strategicInfo.badge === "melhor" && strategicInfo.coverageVerified === false && (
                      <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">
                        ⚠ Cobertura falhou
                      </span>
                    )}
                  </div>
                )}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={downloadCSV}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
                </Button>
                <Button size="sm" variant="outline" onClick={openSaveDialog}>
                  <Bookmark className="mr-1.5 h-3.5 w-3.5" /> Salvar
                </Button>
                <ExportGamesDialog
                  lotterySlug={slug as "mega-sena" | "lotofacil" | "quina" | "dupla-sena"}
                  lotteryName={config.name}
                  games={games}
                  brandLine="LotoTerminal · Combinações"
                  costPerGame={ticketPrice(slug, k)}
                  filenameBase={`combinacoes-${slug}-${games.length}jogos`}
                />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {strategicInfo && (
              <div className="mb-4 rounded-lg border border-lt-border bg-lt-bg2 p-3 text-[12px] leading-relaxed text-muted-foreground">
                {strategicInfo.badge === "exato" && (
                  <p>
                    <strong className="text-foreground">Resultado exato:</strong>{" "}
                    o gerador entregou <strong>{strategicInfo.actual}</strong> jogos,
                    igualando a referência publicada em sistemas de fechamento
                    ({strategicInfo.target} jogos).
                  </p>
                )}
                {strategicInfo.badge === "melhor" && (
                  <>
                    <p>
                      <strong className="text-foreground">Otimizado:</strong>{" "}
                      o gerador encontrou uma solução com{" "}
                      <strong>{strategicInfo.actual}</strong> jogos —{" "}
                      <strong>{strategicInfo.target - strategicInfo.actual}</strong> a{" "}
                      menos que a referência publicada ({strategicInfo.target}).
                      Isso é matematicamente possível: a referência é um{" "}
                      <em>limite superior conhecido</em>, não o mínimo absoluto.
                      Para muitos casos C(v, k, t) o número mínimo de jogos ainda
                      é um problema em aberto.
                    </p>
                    {strategicInfo.coverageVerified && (
                      <p className="mt-2">
                        <strong className="text-emerald-700 dark:text-emerald-300">
                          ✓ Cobertura verificada:
                        </strong>{" "}
                        testamos exaustivamente todos os{" "}
                        <strong>
                          {strategicInfo.coverageTested?.toLocaleString("pt-BR")}
                        </strong>{" "}
                        subconjuntos de {guaranteeHits} dezenas dentro das suas{" "}
                        {selectedNumbers.length} dezenas escolhidas. Cada um deles
                        aparece em pelo menos um jogo gerado — a garantia de{" "}
                        {guaranteeHits} acertos (caso essas {guaranteeHits} dezenas
                        saiam no sorteio) está comprovada.
                      </p>
                    )}
                    {strategicInfo.coverageVerified === false && (
                      <p className="mt-2 text-red-700 dark:text-red-300">
                        ⚠ A verificação falhou — por favor, reporte este caso ao
                        suporte. Não use este resultado.
                      </p>
                    )}
                  </>
                )}
                {strategicInfo.badge === "aproximacao" && (
                  <p>
                    <strong className="text-foreground">Aproximação:</strong>{" "}
                    o gerador entregou <strong>{strategicInfo.actual}</strong>{" "}
                    jogos, acima da referência publicada de{" "}
                    <strong>{strategicInfo.target}</strong>. O resultado mantém
                    a garantia de {guaranteeHits} acertos, mas com alguns jogos
                    extras (heurística não chegou ao ótimo conhecido).
                  </p>
                )}
                <p className="mt-2 text-[11px] opacity-80">
                  <strong>Referências:</strong> La Jolla Covering Repository (LJCR,
                  Gordon &amp; Kuperberg); Covering Repository; D. Bluskov,
                  <em> Combinatorial Designs for Lotteries</em>; tabelas de
                  fechamento publicadas pela literatura brasileira de loterias.
                </p>
              </div>
            )}
            <div className="max-h-[480px] overflow-y-auto">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {games.map((g, i) => (
                  <div key={i} className="rounded-md border border-lt-border bg-lt-bg2 p-3">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Jogo {i + 1}</span>
                      <GameDnaButton numbers={g} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.map((n) => {
                        const isFixed = fixedNumbers.includes(n);
                        const isAnchor = n === pairAnchor;
                        return (
                          <span
                            key={n}
                            title={isAnchor ? "Dezena âncora (entra em todos os jogos)" : undefined}
                            className={cn(
                              "flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-medium ring-2 ring-offset-1 ring-offset-background",
                              isAnchor
                                ? "border-2 border-lt-gold-border bg-lt-gold text-background ring-lt-gold"
                                : isFixed
                                ? "bg-lt-teal text-background ring-lt-teal"
                                : "bg-primary text-primary-foreground ring-primary",
                            )}
                          >
                            {String(n).padStart(2, "0")}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <CheckScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={slug}
        lotteryName={config.name}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          await persistGames({ scope, from, until, specialScheduleId });
        }}
      />
    </div>
  );
}